package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.CaptureDto
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.text.DefaultFontResolver
import dev.lingvo.shared.screenshots.text.FontStack
import dev.lingvo.shared.screenshots.text.Scripts
import dev.lingvo.shared.screenshots.render.effectiveLayers
import dev.lingvo.shared.screenshots.render.RenderInputs
import dev.lingvo.shared.screenshots.render.RenderMode
import dev.lingvo.shared.screenshots.render.TextMeasurer
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.screenshotCaptureImage
import dev.lingvo.site.api.screenshotDesignAssetImage
import dev.lingvo.site.components.studio.canvas.BitmapImage
import dev.lingvo.site.components.studio.preview.DesignLooksPort
import dev.lingvo.site.components.studio.preview.PreviewBitmapStore
import dev.lingvo.site.components.studio.preview.PreviewRenderer
import dev.lingvo.site.components.studio.preview.previewAssetIds
import dev.lingvo.site.pages.app.projects.slug.screenshots.DesignPageData
import dev.lingvo.site.pages.app.projects.slug.screenshots.captionValue
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectAction
import kotlinx.coroutines.CancellationException

/** Owns authenticated inputs for exactly one mounted project/platform; samples never enter this port. */
internal class CanvasDesignPorts(var data: DesignPageData, val looks: DesignLooksPort) {
    val captures = CaptureImageCache()
    private var assets = PreviewBitmapStore()
    private var artworkScope: String? = null
    private val artwork = mutableMapOf<String, BitmapImage>()
    private val claim = ProjectActionClaim.capture(liveProjectRoute(data.project.slug), Session.organizations, ProjectPrivilege.READ)
    private val savedCaptions = mutableMapOf<Pair<String, String>, String>()
    var activeCaption: CaptionSnapshot? = null
    var writable: Boolean = false
    var changed: () -> Unit = {}
    private val fonts = EditorFontReadiness<EditorFontKey>()

    private fun fontKey(design: ScreenshotDesign, view: HistoryView): EditorFontKey {
        val board = design.boards.first { it.deviceClass == view.device }
        var offset = 0
        val requests = board.pages.flatMap { page ->
            val values = visibleText(effectiveLayers(page, board, view.locale, design.schemaVersion)).mapNotNull { layer ->
                val slot = (layer.content as? TextContent.SlotCaption)?.slot ?: layer.base.roleSlot
                val text = text(design, view, layer.content, offset + slot).orEmpty()
                if (text.isEmpty()) null else EditorFontRequest(fontFor(design, view, layer, text), layer.style.weight, layer.style.italic, text)
            }
            offset += page.slotIds.size
            values
        }
        return EditorFontKey(view.locale, requests.distinct())
    }

    fun fontFor(design: ScreenshotDesign, view: HistoryView, layer: TextLayer, text: String): FontStack {
        val style = layer.style
        val script = looks.scripts.scriptFor(view.locale, text)
        val family = looks.templates().firstOrNull { it.id == design.templateId }?.families?.get(style.role)
        return if (looks.fonts === DefaultFontResolver) DefaultFontResolver.stack(style.role, style.family,
            design.brand, family, script, Scripts.hasEmoji(text), style.weight, style.italic)
        else looks.fonts.stack(style.role, style.family, design.brand, family, script, Scripts.hasEmoji(text))
    }

    fun fontsReady(design: ScreenshotDesign, view: HistoryView): Boolean = fonts.isReady(fontKey(design, view))

    suspend fun prepareFonts(design: ScreenshotDesign, view: HistoryView) {
        val key = fontKey(design, view)
        if (fonts.isReady(key)) return
        val token = fonts.begin(key)
        try {
            for (request in key.requests) check(looks.ensureFont(request.stack, request.weight, request.italic, request.text)) {
                "The editor caption font could not load" // i18n-ignore: caught and replaced by the locale-specific editor status
            }
            fonts.complete(token, key, true)
        } catch (failure: Exception) {
            fonts.complete(token, key, false)
            throw failure
        }
    }

    suspend fun <T> read(block: suspend () -> T): T = requestProjectAction(
        claim ?: throw CancellationException("The screenshot project is no longer active"),
        { liveProjectRoute(data.project.slug) }, { Session.organizations }, block,
    )

    val keys = CaptionKeysPort { snapshot, value ->
        if (!writable) throw CancellationException("The screenshot editor is read-only")
        read { KeysCaptionPort.write(snapshot, value) }.also {
            savedCaptions[it.locale to it.key] = it.value
            changed()
        }
    }

    fun capture(view: HistoryView, slotKey: String): CaptureDto? {
        val slot = data.plan.slots.firstOrNull { it.key == slotKey }
        val locale = if (slot?.localizedUi == false) data.project.sourceLocale else view.locale
        return data.captures.firstOrNull { it.slotKey == slotKey && it.deviceClass.id == view.device &&
            it.locale == locale && it.supersededAt == null && !it.blobMissing }
    }

    private fun caption(locale: String, key: String): String? {
        activeCaption?.takeIf { it.locale == locale && it.key == key }?.let { return it.draft }
        return savedCaptions[locale to key] ?: captionValue(data, locale, key)
    }

    fun text(design: ScreenshotDesign, view: HistoryView, content: TextContent, index: Int): String? {
        val key = when (content) {
            is TextContent.Literal -> return content.text
            is TextContent.Key -> content.key
            is TextContent.SlotCaption -> design.boards.first { it.deviceClass == view.device }.pages.flatMap { it.slotIds }.getOrNull(index)
        } ?: return null
        return caption(view.locale, key) ?: caption(data.project.sourceLocale, key)
    }

    fun inputs(design: ScreenshotDesign, view: HistoryView, measurer: TextMeasurer): RenderInputs {
        return RenderInputs(view.locale, RenderMode.EDITOR, { content, index -> text(design, view, content, index) },
            { source, index -> image(design, view, source, index) },
            measurer, looks.segmenter, if (fontsReady(design, view)) looks.fonts else EditorFallbackFonts, looks.scripts,
            looks.templates().firstOrNull { it.id == design.templateId }?.families.orEmpty())
    }

    private fun image(design: ScreenshotDesign, view: HistoryView, source: ImageSource, index: Int): BitmapImage? = when (source) {
        is ImageSource.Capture -> design.boards.first { it.deviceClass == view.device }.pages.flatMap { it.slotIds }
            .getOrNull(index)?.let { capture(view, it) }?.let { captures.get("${it.id}:${it.sha256}") }
        is ImageSource.Asset -> artwork[source.assetId]
        ImageSource.BrandLogo -> design.brand.logoAssetId?.let { artwork[it] }
    }

    fun thumbnail(design: ScreenshotDesign, view: HistoryView, slot: String) = PreviewRenderer(
        looks, if (fontsReady(design, view)) looks.fonts else EditorFallbackFonts,
    ).render(
        design, design.boards.first { it.deviceClass == view.device }.id, slot, view.locale,
        { content, index -> text(design, view, content, index) }, { source, index -> image(design, view, source, index) },
    )

    suspend fun prepare(design: ScreenshotDesign, view: HistoryView) {
        val board = design.boards.first { it.deviceClass == view.device }
        for (slot in board.pages.flatMap { it.slotIds }) capture(view, slot)?.let { capture ->
            val key = "${capture.id}:${capture.sha256}"
            if (captures.get(key) == null) {
                val blob = read { ApiClient.screenshotCaptureImage(data.project.slug, design.platform, capture.id) }
                captures.load(key, blob, capture.width, capture.height, 360.0, 720.0)
            }
        }
        val scope = "${board.id}:${view.locale}"
        if (scope != artworkScope) {
            assets.close(); assets = PreviewBitmapStore(); artwork.clear(); artworkScope = scope
        }
        val assetIds = board.pages.flatMap { previewAssetIds(it.layers, design.brand.logoAssetId) }.distinct()
        check(assetIds.size <= 64) { "This canvas exceeds the artwork preview budget" } // i18n-ignore: internal resource diagnostic
        for (id in assetIds) {
            artwork[id] = assets.load("asset:$id") { read { ApiClient.screenshotDesignAssetImage(data.project.slug, design.platform, id) } }
        }
    }

    fun close() { captures.clear(); assets.close(); artwork.clear(); activeCaption = null; changed = {} }
}

private data class EditorFontKey(val locale: String, val requests: List<EditorFontRequest>)
private data class EditorFontRequest(val stack: FontStack, val weight: Int, val italic: Boolean, val text: String)

private fun visibleText(layers: List<Layer>): List<TextLayer> = layers.filterNot { it.base.hidden || it.base.opacity == 0.0 }.flatMap {
    when (it) { is TextLayer -> listOf(it); is GroupLayer -> visibleText(it.children); else -> emptyList() }
}
